import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { CardElement, useElements } from '@stripe/react-stripe-js';
import { Checkbox } from 'primereact/checkbox';
import CustomInput from './CustomInput';
import CustomDropDown from './CustomDropDown';
import fetchStatesDropdownOptions from '../services/states';

import './checkoutSecurePayment.scss';

import CardPeacock from './PeacockCheckoutCard';
import CardCheckout from './CheckoutCard';
import useStripeCard from '../hooks/useStripeCard';
import CardsImg from '../assets/images/cards.svg';
import getDemoLink from '../utils/helpers';

function CheckoutSecurePayment({
  selectedCard,
  updateForm,
  values,
  setForm,
  devicePrice,
  devicePriceFee,
  shippingPriceFee,
  formErrors,
  renderButtons,
  showFireCode = false,
}) {
  const Card = showFireCode ? CardPeacock : CardCheckout;
  const [cardError, setCardError] = useState('');
  const [initialValues] = useState(values);
  const [USStatesDropdownOptions, setUSStatesDropdownOptions] = useState([]);

  useEffect(async () => {
    const options = await fetchStatesDropdownOptions();
    setUSStatesDropdownOptions(options);
  }, []);

  const elements = useElements();

  const [createPaymentMethod] = useStripeCard(elements.getElement(CardElement), (pm, error) => {
    if (error !== null) {
      console.error(error);
      setCardError(error?.message);
      updateForm({
        target: {
          name: 'paymentMethod',
          value: undefined,
        },
      });
    } else {
      setCardError('');
      // Payment method details are in the pm Object
      updateForm({
        target: {
          name: 'paymentMethod',
          value: pm,
        },
      });
    }
  });

  const handleChange = e => {
    updateForm(e);
  };

  return (
    <div id="secure-container">
      <div className="main_container grid">
        <div className="padding_0 data_container col-7">
          <div className="col-12 flex align-items-center header">
            <div className="header_number">3</div>
            <div className="header_text_box">
              <p className="step_text_mobile">Step 3 of 4</p>
              <h2 className="header_title">Secure Payment</h2>
            </div>
          </div>
          <div className="form_conatiner">
            <div className="sub-header col-12 padding_0">
              <p className="sub_header_text">Please complete payment information & our team will begin configuring your device</p>
            </div>
            <div className="sub-title col-12 padding_0">
              <h3>Billing Information</h3>
            </div>
            <div className="col-12 field-radiobutton padding_0">
              <Checkbox
                inputId="useBusinessAddressForBilling"
                name="useBusinessAddressForBilling"
                checked={values.useBusinessAddressForBilling}
                onChange={e => {
                  if (e.checked) {
                    setForm({
                      ...values,
                      billingCity: initialValues.city,
                      billingAddress: initialValues.businessAddress,
                      billingAddress2: initialValues.businessAddress2,
                      billingZipCode: initialValues.zipCode,
                      billingState: initialValues.state,
                      billingCountry: initialValues.country,
                      useBusinessAddressForBilling: true,
                    });
                  } else {
                    setForm({
                      ...values,
                      billingCity: '',
                      billingAddress: '',
                      billingAddress2: '',
                      billingZipCode: '',
                      billingState: '',
                      billingCountry: 'United States',
                      useBusinessAddressForBilling: false,
                    });
                  }
                }}
              />
              <span>
                <label htmlFor="binary">Same as business address</label>
              </span>
            </div>
            <div className="grid ">
              <div className="col-12 field">
                <div id="billing-addres">
                  <span className="block label required_icon">Address Line 1</span>
                  <CustomInput
                    error={formErrors.billingAddress}
                    errorMessage={formErrors.billingAddress}
                    onChange={handleChange}
                    name="billingAddress"
                    aria-describedby="username2-help"
                    className="block col-6 padding_0"
                    value={values.billingAddress}
                    placeholder="Street Address..."
                  />
                </div>
              </div>
              <div className="col-12 field">
                <div id="billing-addres">
                  <span className="block label">Address Line 2</span>
                  <CustomInput
                    error={formErrors.billingAddress2}
                    errorMessage={formErrors.billingAddress2}
                    onChange={handleChange}
                    name="billingAddress2"
                    aria-describedby="username2-help"
                    className="block col-6 padding_0"
                    value={values.billingAddress2}
                    placeholder="Street Address..."
                  />
                </div>
              </div>
              <div className="flex col-12 padding_0 inputs_box">
                <div className="flex col-6 padding_0">
                  <div className="col-8 field">
                    <span className="block label required_icon">City</span>
                    <CustomInput
                      error={formErrors.billingCity}
                      errorMessage={formErrors.billingCity}
                      className="block"
                      value={values.billingCity}
                      onChange={handleChange}
                      name="billingCity"
                      placeholder="City..."
                    />
                  </div>
                  <div className="col-4 field">
                    <div id="zip-code">
                      <span className="block label required_icon">Zip Code</span>
                      <CustomInput
                        error={formErrors.billingZipCode}
                        errorMessage={formErrors.billingZipCode}
                        onChange={handleChange}
                        name="billingZipCode"
                        aria-describedby="username2-help"
                        className="block"
                        value={values.billingZipCode}
                        placeholder="Zip Code..."
                      />
                    </div>
                  </div>
                </div>
                <div className="col-6 field">
                  <span className="block label required_icon">State / Province</span>
                  <CustomDropDown
                    error={formErrors.billingState}
                    errorMessage={formErrors.billingState}
                    className="block"
                    value={values.billingState}
                    options={USStatesDropdownOptions}
                    onChange={handleChange}
                    placeholder={USStatesDropdownOptions.length === 0 ? 'Loading' : 'State / Province...'}
                    name="billingState"
                  />
                </div>
              </div>
              <div className="col-12 field">
                <span className="block label">Country</span>
                <CustomInput
                  error={formErrors.billingCountry}
                  errorMessage={formErrors.billingCountry}
                  className="block"
                  value={values.billingCountry}
                  onChange={handleChange}
                  name="billingCountry"
                  placeholder="Country..."
                  disabled
                />
                <div className="col-12 paraf padding_0">
                  <p>
                    <b>Note: </b>If you live outside of the United States, please contact our&nbsp;
                    <a href={getDemoLink(showFireCode)} target="_blank" rel="noopener noreferrer">
                      Customer Success
                    </a>
                    &nbsp;team so we can get you set up
                  </p>
                </div>
              </div>
              <div className="col-12 field">
                <div id="credit-card">
                  <span className="block label required_icon">Credit card</span>
                  <div className="stripe_box">
                    <CardElement
                      name="paymentMethod"
                      className="card-element block"
                      onChange={createPaymentMethod}
                      options={{
                        hidePostalCode: true,
                        style: {
                          base: {
                            iconColor: '#495057',
                            color: '#495057',
                            colorBackground: '#ffffff',
                            fontWeight: '400',
                            fontFamily:
                              'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            fontSize: '14px',
                            fontSmoothing: 'antialiased',
                            ':-webkit-autofill': {
                              color: 'black',
                            },
                            '::placeholder': {
                              color: '#495057',
                            },
                          },
                          invalid: {
                            iconColor: 'red',
                            color: 'red',
                          },
                        },
                      }}
                    />
                  </div>
                  <span>{cardError}</span>
                </div>
              </div>
            </div>
            <div className="img_box flex flex-column align-items-start">
              <img className="cards_img" src={CardsImg} alt="box" />
            </div>
            {renderButtons()}
          </div>
        </div>
        <div className="card_container col-5 align-self-start">
          <Card
            id={selectedCard?.id}
            title={selectedCard?.title}
            price={selectedCard?.priceText}
            checkList={selectedCard?.checklistP}
            icon={selectedCard?.icon}
            devicePrice={devicePrice}
            numberOfDevices={values.numberOfDevices}
            devicePriceFee={devicePriceFee}
            shippingPriceFee={shippingPriceFee}
            showFireCode
            peacockFee={values.fireCodePrice}
            billingPlan={values.billingPlan}
          />
        </div>
      </div>
    </div>
  );
}

CheckoutSecurePayment.propTypes = {
  selectedCard: PropTypes.objectOf(PropTypes.any),
  values: PropTypes.shape({
    useBusinessAddressForBilling: PropTypes.bool,
    phoneNumber: PropTypes.string,
    hasTag: PropTypes.string,
    billingAddress: PropTypes.string.isRequired,
    billingAddress2: PropTypes.string,
    numberOfDevices: PropTypes.number.isRequired,
    billingZipCode: PropTypes.string.isRequired,
    billingCity: PropTypes.string.isRequired,
    billingState: PropTypes.string.isRequired,
    billingCountry: PropTypes.string.isRequired,
    fireCodePrice: PropTypes.number,
    source: PropTypes.string,
    billingPlan: PropTypes.string,
  }).isRequired,
  setForm: PropTypes.func.isRequired,
  devicePrice: PropTypes.number,
  devicePriceFee: PropTypes.number,
  shippingPriceFee: PropTypes.number,
  updateForm: PropTypes.func.isRequired,
  formErrors: PropTypes.objectOf(PropTypes.any),
  renderButtons: PropTypes.any,
  showFireCode: PropTypes.bool,
};

CheckoutSecurePayment.defaultProps = {
  devicePrice: undefined,
  devicePriceFee: undefined,
  shippingPriceFee: undefined,
  formErrors: undefined,
  selectedCard: undefined,
  renderButtons: () => {},
  showFireCode: false,
};

export default CheckoutSecurePayment;
